import { Component, OnInit, Optional, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastService } from '../../../../core/services/toast.service';
import { OrganizationService } from '../../../../core/services/organization/organization.service';
import { TimezoneService } from '../../../../core/services/organization/timezone.service';
import { NgxIntlTelInputModule } from 'ngx-intl-tel-input';
import {
  CountryISO,
  SearchCountryField,
  PhoneNumberFormat,
} from 'ngx-intl-tel-input';
import parsePhoneNumberFromString from 'libphonenumber-js';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { PhoneInputDirective } from '../../../../shared/directives/phone-input.directive';
import { SelectComponent } from 'app/shared/components/select/select.component';

@Component({
  selector: 'app-edit-organization',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    NgxIntlTelInputModule,
    TranslateModule,
    PhoneInputDirective,
    SelectComponent,
  ],
  templateUrl: './edit-organization.component.html',
  styleUrl: './edit-organization.component.scss',
})
export class EditOrganizationComponent implements OnInit {
  @Input() data?: { organizationId?: string };
  editForm!: FormGroup;
  isSubmitting = false;
  isLoading = true;
  organizationId: string = '';
  timeZones: { name: string; label: string }[] = [];

  CountryISO = CountryISO;
  SearchCountryField = SearchCountryField;
  PhoneNumberFormat = PhoneNumberFormat;
  defaultCountry: CountryISO = CountryISO.Turkey;

  constructor(
    private organizationService: OrganizationService,
    private translate: TranslateService,
    private toastService: ToastService,
    private router: Router,
    private fb: FormBuilder,
    private timezoneService: TimezoneService,
    @Optional() public activeModal?: NgbActiveModal,
  ) {
    const userLang = localStorage.getItem('app-lang') || 'en-US';
    this.translate.use(userLang);
    this.initEditForm();
  }

  ngOnInit(): void {
    this.timeZones = this.timezoneService.getAllTimezones();
    this.organizationId = this.data?.organizationId || '';

    if (this.organizationId) {
      this.loadOrganization();
    } else {
      console.error('EditOrganizationComponent: organizationId not found');
      this.isLoading = false;
    }
  }

  initEditForm(): void {
    this.editForm = this.fb.group({
      name: [
        '',
        [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(100),
          Validators.pattern(/^[a-zA-ZşŞıİçÇöÖüÜĞğ\s']+$/),
        ],
      ],
      customerCode: [
        '',
        [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(100),
        ],
      ],
      email: [
        '',
        [Validators.required, Validators.email, Validators.maxLength(50)],
      ],
      phone: ['', Validators.required],
      timeZone: [null, [Validators.required, Validators.maxLength(70)]],
      address: ['', [Validators.required, Validators.maxLength(1000)]],
      contactPerson: [
        '',
        [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(100),
          Validators.pattern(/^[a-zA-ZşŞıİçÇöÖüÜĞğ\s']+$/),
        ],
      ],
      isAssessmentActive: [false],
    });
  }

  loadOrganization(): void {
    this.isLoading = true;
    this.organizationService
      .getOrganizationById(this.organizationId)
      .subscribe({
        next: (res) => {
          if (res.data) {
            const org = res.data;
            this.editForm.patchValue({
              name: org.name,
              customerCode: org.customerCode,
              email: org.email,
              contactPerson: org.contactPerson,
              address: org.address,
              phone: org.phone,
              timeZone: org.timeZone,
              isAssessmentActive: org.isAssessmentActive,
            });
          }
          this.isLoading = false;
        },
      });
  }

  submitEdit(): void {
    if (this.editForm.invalid) {
      this.editForm.markAllAsTouched();
      return;
    }

    if (!this.organizationId) return;

    this.isSubmitting = true;
    const raw = this.editForm.getRawValue();
    const phoneInput = raw.phone;

    let finalPhone = '';
    let finalCountryCode = '';

    if (phoneInput && typeof phoneInput === 'object') {
      finalPhone = phoneInput.number
        ? phoneInput.number.replace(/\D/g, '')
        : '';
      finalCountryCode = phoneInput.dialCode;
    } else if (typeof phoneInput === 'string' && phoneInput.trim() !== '') {
      const parsed = parsePhoneNumberFromString(phoneInput);
      if (parsed) {
        finalPhone = parsed.nationalNumber;
        finalCountryCode = '+' + parsed.countryCallingCode;
      } else {
        finalPhone = phoneInput;
      }
    }

    const updateRequest = {
      id: this.organizationId,
      name: raw.name,
      customerCode: raw.customerCode,
      contactPerson: raw.contactPerson,
      email: raw.email,
      address: raw.address,
      timeZone: raw.timeZone,
      phone: finalPhone,
      countryCode: finalCountryCode,
      isAssessmentActive: raw.isAssessmentActive,
    };

    this.organizationService.updateOrganization(updateRequest).subscribe({
      next: (res) => {
        this.toastService.showToast(
          this.translate.instant('organization.updateSuccess'),
          'success',
        );
        if (this.activeModal) {
          this.activeModal.close('success');
        } else {
          this.router.navigate(['/organization/list']);
        }
        this.isSubmitting = false;
      },
      error: () => {
        this.isSubmitting = false;
      },
    });
  }

  isControlInvalid(controlName: string): boolean {
    const control = this.editForm.get(controlName);
    return control
      ? control.invalid && (control.touched || control.dirty)
      : false;
  }

  onCancel(): void {
    if (this.activeModal) {
      this.activeModal.dismiss('cancel');
    } else {
      this.router.navigate(['/organization/list']);
    }
  }
}
